import { useQueryClient } from '@tanstack/react-query'
import { Modal, SpinnerWithBackground, Text } from '@viapeople/components'
import { useState } from 'react'
import { type Column, GenericTable } from '../../../components/GenericTable'
import { formatDateTime } from '../../../utils/formatters'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import type { UserFile, UserFileListInput, UserFilesModalProps } from '../types'

export const UserFilesModal = ({ isOpen, onClose, organizationId }: UserFilesModalProps) => {
	const queryClient = useQueryClient()
	const toast = useToast()
	const [isLoading, setIsLoading] = useState(false)

	const handleDownload = async (fileId: string) => {
		try {
			setIsLoading(true)

			const result = await queryClient.fetchQuery(
				trpc.admin.userFiles.requestFileDownload.queryOptions({
					fileId,
					organizationId,
				}),
			)

			if (result?.readUrl) {
				window.open(result.readUrl, '_blank')
			} else {
				toast.showToast('Could not retrieve download URL', 'error')
			}
		} catch (error: unknown) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error'
			toast.showToast(`Error requesting file download: ${errorMessage}`, 'error')
		} finally {
			setIsLoading(false)
		}
	}

	const columns: Column<UserFile>[] = [
		{
			label: 'File name',
			field: 'originalName',
			sortable: false,
			render: (item: UserFile) => (
				<Text
					variant='xsmall'
					className='text-primary-600 cursor-pointer'
					onClick={(e) => {
						e.stopPropagation()
						handleDownload(item.id)
					}}>
					{item.filename}
				</Text>
			),
		},
		{
			label: 'Upload date',
			field: 'createdAt',
			sortable: false,
			render: (item: UserFile) => formatDateTime(item.createdAt),
		},
		{
			label: 'Uploaded by',
			field: 'filename',
			sortable: false,
			render: (item: UserFile) => `${item.uploadedByFirstName} ${item.uploadedByLastName}`,
		},
	]

	return (
		<Modal isOpen={isOpen} onClose={onClose} title='Data Files' fullContent className='md:w-300 max-w-[1110px] mx-12'>
			{isLoading && <SpinnerWithBackground />}
			<GenericTable<UserFile, UserFileListInput>
				onRowClick={(item: UserFile) => handleDownload(item.id)}
				columns={columns}
				isInternal
				className='min-h-[460px]'
				showSearch={false}
				queryOptionsGenerator={(options) =>
					trpc.admin.userFiles.list.queryOptions({ ...options, organizationId: organizationId as string })
				}
			/>
		</Modal>
	)
}

export default UserFilesModal
